/** Billing data is authoritative; entry/reason/quote are display-only hints. */
export interface ConnectBillingStatus {
  plan_slug: string | null
  plan_name: string | null
  credits_per_month: number | null
  balance: number
  is_paid: boolean
  can_buy_credits: boolean
  payment_issue: boolean
  has_stripe_customer: boolean
}

export function connectBillingAction(
  status: ConnectBillingStatus,
  required = 1,
  product: 'soku' | 'toolfare' = 'soku',
) {
  if (status.payment_issue) return 'payment' as const
  if (!status.is_paid && product !== 'toolfare') return 'subscribe' as const
  if (
    status.credits_per_month === -1 ||
    status.balance >= Math.max(1, required)
  )
    return 'ready' as const
  return status.can_buy_credits ? ('topup' as const) : ('support' as const)
}

const RESUME_TTL = 60 * 60 * 1000
const resumeKey = (org: string) => `soku.connect.checkout:${org}`

/** A same-tab continuation also covers a resumed Stripe session with older return URLs. */
export function rememberConnectCheckout(org: string, path: string) {
  try {
    sessionStorage.setItem(
      resumeKey(org),
      JSON.stringify({ path, expires: Date.now() + RESUME_TTL }),
    )
  } catch {
    /* Storage can be unavailable in private browsers. */
  }
}

export function consumeConnectCheckout(org: string): string | null {
  try {
    const raw = sessionStorage.getItem(resumeKey(org))
    sessionStorage.removeItem(resumeKey(org))
    if (!raw) return null
    const entry = JSON.parse(raw) as { path: string; expires: number }
    return entry.expires > Date.now() &&
      entry.path.startsWith(`/o/${org}/b/`) &&
      entry.path.split('?')[0].endsWith('/mcp-cli/upgrade')
      ? entry.path
      : null
  } catch {
    return null
  }
}
